import { P } from 'pino';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface DropdownOption {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  description?: string;
  statusName?: string;
  statusId?: string | number;
  planStatus?: {
    created: boolean;
    approved: boolean;
    rejected: boolean;
  };
}

export interface CustomDropdownProps {
  label: string;
  options: DropdownOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  width?: string;
  variant?: 'filled' | 'surface';
  isSearchEnabled?: boolean;
  className?: string;
  noBorder?: boolean;
  isTextInput?: boolean;
  labelClassName?: string;
  showDescription?: boolean;
  buttonClassName?: string;
  context?: string;
  labelType?: 'required' | 'optional';
}

// Extracted component for status badge
interface StatusBadgeProps {
  statusName: string;
  planStatus?: {
    created: boolean;
    approved: boolean;
    rejected: boolean;
  };
}

function StatusBadge({ statusName, planStatus }: StatusBadgeProps) {
  const getStatusClasses = () => {
    if (planStatus?.rejected) {
      return 'bg-tertiaryDefaultLight text-tertiaryDefault border-btnColor';
    }
    if (planStatus?.approved) {
      return 'border-primaryDefault bg-primaryExtraLight text-primaryDefault';
    }
    return 'border-secondaryDefault bg-secondaryDefaultExtraLight text-secondaryDefault';
  };

  return (
    <span className={`bodyXs rounded-lg border px-2 ${getStatusClasses()}`}>{statusName}</span>
  );
}

// Extracted component for option content
interface OptionContentProps {
  option: DropdownOption;
  isSelected: boolean;
  showDescription: boolean;
}

function OptionContent({ option, isSelected, showDescription }: OptionContentProps) {
  const getTextColorClass = (baseClass: string) => {
    return isSelected
      ? 'bg-primaryExtraLight text-primarySemiDark'
      : `${baseClass} hover:bg-neutralUltraLight`;
  };

  if (showDescription) {
    return (
      <p className="flex flex-col items-start">
        <span className="bodySmSemibold mb-1">{option.label}</span>
        <span className={`bodyXs text-fontColor ${getTextColorClass('text-neutralDark')}`}>
          {option.description}
        </span>
      </p>
    );
  }

  return (
    <p className="flex items-center justify-between gap-1.5">
      <span className="bodySm text-neutralDark">{option.label}</span>
      {option.statusName && (
        <StatusBadge
          statusName={option.statusName}
          planStatus={option.planStatus}
        />
      )}
    </p>
  );
}

// Extracted component for dropdown option item
interface DropdownOptionItemProps {
  option: DropdownOption;
  isSelected: boolean;
  showDescription: boolean;
  onSelect: (value: string | number) => void;
}

function DropdownOptionItem({
  option,
  isSelected,
  showDescription,
  onSelect,
}: DropdownOptionItemProps) {
  const handleClick = () => onSelect(option.value);

  const getItemClasses = () => {
    const baseClasses =
      'transition-color mx-2 my-1 flex cursor-pointer items-center rounded-md px-4 py-2 min-h-[44px] duration-300';
    const selectedClasses = isSelected
      ? 'bg-primaryExtraLight text-primarySemiDark'
      : 'text-neutralDark hover:bg-neutralUltraLight';
    return `${baseClasses} ${selectedClasses}`;
  };

  return (
    <div
      data-testid={`dropdown-option-${option.value}`}
      data-value={option.label}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={`${getItemClasses()} focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryDefault`}
    >
      {option.icon && <span className="mr-2">{option.icon}</span>}
      <OptionContent
        option={option}
        isSelected={isSelected}
        showDescription={showDescription}
      />
    </div>
  );
}

// Extracted component for dropdown menu
interface DropdownMenuProps {
  isSearchEnabled: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  loading: boolean;
  filteredOptions: DropdownOption[];
  value: string | number;
  showDescription: boolean;
  onSelect: (value: string | number) => void;
  t: (key: string) => string;
}

function DropdownMenu({
  isSearchEnabled,
  searchTerm,
  setSearchTerm,
  searchInputRef,
  loading,
  filteredOptions,
  value,
  showDescription,
  onSelect,
  t,
}: DropdownMenuProps) {
  return (
    <div className="absolute left-0 top-full z-10 mt-1 max-h-60 w-full min-w-48 overflow-auto rounded-xl border border-neutralLight bg-neutralWhite py-1 shadow-lg">
      {/* Search Field */}
      {isSearchEnabled && (
        <div className="sticky top-0 bg-neutralWhite px-2 py-2">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className="min-h-[44px] w-full rounded-lg border border-neutralLight p-2 text-base outline-none focus:border-neutralMedium focus:ring-1 focus:ring-primaryExtraLight"
          />
        </div>
      )}

      {/* No Results */}
      {!loading && filteredOptions.length === 0 && (
        <div className="flex justify-center py-2 text-neutralDefault">{t('no_results_found')}</div>
      )}

      {/* Options */}
      {!loading &&
        filteredOptions.map((option) => (
          <DropdownOptionItem
            key={option.value}
            option={option}
            isSelected={option.value === value}
            showDescription={showDescription}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

// Extracted component for dropdown button
interface DropdownButtonProps {
  onClick: () => void;
  disabled: boolean;
  error: boolean;
  getSizeClasses: () => string;
  noBorder: boolean;
  buttonClassName: string;
  displayValue: string;
  selectedOption?: DropdownOption;
  loading: boolean;
  context?: string;
}

function DropdownButton({
  onClick,
  disabled,
  error,
  getSizeClasses,
  noBorder,
  buttonClassName,
  displayValue,
  selectedOption,
  loading,
  context,
}: DropdownButtonProps) {
  const getButtonClasses = () => {
    const baseClasses = `relative flex h-11 lg:h-12 w-full items-center justify-between rounded-xl border ${error ? 'border-btnColor' : 'border-neutralLight'} ${getSizeClasses()} px-4 text-left`;
    const disabledClasses = disabled
      ? 'cursor-not-allowed opacity-50'
      : `cursor-pointer transition-all duration-200 ${noBorder ? 'hover:bg-transparent' : ''}`;
    return `${baseClasses} ${disabledClasses} ${buttonClassName ?? ''}`;
  };

  const getTextClasses = () => {
    if (!selectedOption) return 'text-neutralDefault';
    if (noBorder) return 'text-white';
    return 'font-medium text-gray-900';
  };

  return (
    <button
      data-test-id={context}
      type="button"
      onClick={onClick}
      className={getButtonClasses()}
      style={noBorder ? { border: 'none' } : {}}
      disabled={disabled}
    >
      <span className={`block truncate ${getTextClasses()}`}>{displayValue}</span>

      {/* Dropdown Icon */}
      {!disabled && (
        <span className="pointer-events-none ml-2">
          {loading ? (
            <svg
              className="h-5 w-5 animate-spin text-neutralDefault"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-neutralDefault"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
      )}
    </button>
  );
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  loading = false,
  error = false,
  helperText,
  disabled = false,
  placeholder = '',
  size = 'medium',
  variant = 'surface',
  width,
  isSearchEnabled = true,
  className = '',
  noBorder = false,
  isTextInput = false,
  labelClassName = '',
  showDescription = false,
  buttonClassName = '',
  labelType = 'optional',
  planStatus,
  context,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && isSearchEnabled && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isSearchEnabled]);

  const { t } = useTranslation(['common']);

  const filteredOptions = options.filter((option) =>
    (option.label ?? '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find((option) => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder || '';

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'py-1 text-sm';
      case 'large':
        return 'py-3 text-base';
      case 'medium':
      default:
        return 'py-2 text-base';
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (selectedValue: string | number) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div
      className={`relative w-full ${width ? width : 'w-full'} ${className} mb-3`}
      ref={dropdownRef}
    >
      {label && (
        <label className={`bodySm mb-1 block min-h-[22px] text-neutralDark ${labelClassName}`}>
          {label}
          {labelType === 'required' && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Text Input Mode */}
      {isTextInput ? (
        <input
          type="text"
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`h-12 w-full rounded-xl border ${error ? 'border-btnColor' : 'border-neutralLight'} px-4 ${getSizeClasses()} ${disabled ? 'cursor-not-allowed opacity-50' : `transition-all duration-200 ${noBorder ? 'hover:bg-transparent' : 'hover:bg-neutralExtraLight'}`}`}
        />
      ) : (
        <>
          {/* Dropdown Button */}
          <DropdownButton
            context={`${context}-button`}
            onClick={handleToggle}
            disabled={disabled}
            data-testid={`${context}-button`}
            data-value={value}
            error={error}
            getSizeClasses={getSizeClasses}
            noBorder={noBorder}
            buttonClassName={buttonClassName}
            displayValue={displayValue}
            selectedOption={selectedOption}
            loading={loading}
          />

          {/* Dropdown Menu */}
          {isOpen && (
            <DropdownMenu
              isSearchEnabled={isSearchEnabled}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchInputRef={searchInputRef}
              loading={loading}
              filteredOptions={filteredOptions}
              value={value}
              showDescription={showDescription}
              onSelect={handleSelect}
              t={t}
            />
          )}
        </>
      )}

      {/* Helper Text */}
      {helperText && (
        <p className={`mt-1 text-sm ${error ? 'text-btnColor' : 'text-neutralDefault'}`}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default CustomDropdown;
